import React, { useState, useEffect } from "react";
import {
    Box,
    FormControlLabel,
    Typography,
    CircularProgress,
    Switch,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const Settings = ({ currentUser, updateSettings }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const navigate = useNavigate();

    const requiredConfirmationText = "DELETE MY PROFILE";

    useEffect(() => {
        if (currentUser && currentUser.settings) {
            setSettings({ ...currentUser.settings });
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                if (currentUser?.settings) {
                    setSettings({ ...currentUser.settings });
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [currentUser?.settings]);

    const handleSettingChange = async (key) => {
        if (!settings) return;

        const newSettings = { ...settings, [key]: !settings[key] };
        setSettings(newSettings);
        updateSettings(newSettings);

        const token = localStorage.getItem("token");
        if (!token) {
            console.warn("⚠️ No token found, skipping API request.");
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/update-settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(newSettings),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update settings: ${errorText}`);
            }

            const result = await response.json();

            if (result.settings) {
                setSettings(result.settings);
                updateSettings(result.settings);
            } else {
                console.warn("⚠️ Unexpected response structure, using local state.");
            }
        } catch (error) {
            console.error("❌ Error updating settings:", error);
        }
    };

    const handleDeleteProfile = async () => {
        if (confirmationText !== requiredConfirmationText) {
            setDeleteError("Please type the exact confirmation text");
            return;
        }

        setDeleteLoading(true);
        setDeleteError("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-profile`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                },
            });

            const result = await response.json();

            if (response.ok) {
                localStorage.removeItem("token");
                localStorage.removeItem("recentInteractions");

                alert("Your profile has been successfully deleted. You will now be logged out.");

                window.location.href = "/";
            } else {
                setDeleteError(result.message || "Failed to delete profile");
            }
        } catch (error) {
            console.error("Error deleting profile:", error);
            setDeleteError("An error occurred while deleting your profile");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setConfirmationText("");
        setDeleteError("");
    };

    if (loading || settings === null) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} sx={{ my: 4 }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={settings["Appointment Notifications"] ?? false}
                        onChange={() => handleSettingChange("Appointment Notifications")}
                        color="success"
                    />
                }
                label="Appointment Notifications"t
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={settings["Message Notifications"] ?? false}
                        onChange={() => handleSettingChange("Message Notifications")}
                        color="success"
                    />
                }
                label="Message Notifications"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={settings["New Contact Notifications"] ?? false}
                        onChange={() => handleSettingChange("New Contact Notifications")}
                        color="success"
                    />
                }
                label="New Contact Notifications"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={settings["Notification Sounds"] ?? false}
                        onChange={() => handleSettingChange("Notification Sounds")}
                        color="success"
                    />
                }
                label="Notification Sounds"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={settings["Miscellaneous Sounds"] ?? false}
                        onChange={() => handleSettingChange("Miscellaneous Sounds")}
                        color="success"
                    />
                }
                label="Miscellaneous Sounds"
            />

            <Divider sx={{ my: 3, backgroundColor: "#333" }} />

            { }
            <Box sx={{
                border: "2px solid #d32f2f",
                borderRadius: "8px",
                p: 3,
                backgroundColor: "rgba(211, 47, 47, 0.05)"
            }}>
                <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: "bold", mb: 2 }}>
                    Danger Zone
                </Typography>

                <Typography variant="body2" sx={{ color: "#ccc", mb: 2 }}>
                    Once you delete your profile, there is no going back. This action cannot be undone.
                </Typography>

                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        "&:hover": {
                            borderColor: "#f44336",
                            backgroundColor: "rgba(211, 47, 47, 0.1)"
                        }
                    }}
                >
                    Delete Profile
                </Button>
            </Box>

            { }
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: "#d32f2f", fontWeight: "bold" }}>
                    ⚠️ Delete Profile - Final Warning
                </DialogTitle>

                <DialogContent>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            This action is PERMANENT and IRREVERSIBLE!
                        </Typography>
                    </Alert>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        You are about to permanently delete your profile. This will:
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                            • Permanently delete all your account data
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                            • Remove all your interaction logs and history
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                            • Delete your booking records and assignments
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                            • Log you out immediately
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                            • Make your username available for others to use
                        </Typography>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2, fontWeight: "bold" }}>
                        To confirm deletion, type: <code style={{
                            backgroundColor: "#333",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            color: "#d32f2f"
                        }}>
                            {requiredConfirmationText}
                        </code>
                    </Typography>

                    <TextField
                        fullWidth
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder={requiredConfirmationText}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderColor: "#d32f2f",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#f44336",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#d32f2f",
                                },
                            },
                        }}
                    />

                    {deleteError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {deleteError}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        disabled={deleteLoading}
                        sx={{ color: "#98FF98" }}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleDeleteProfile}
                        disabled={
                            deleteLoading ||
                            confirmationText !== requiredConfirmationText
                        }
                        variant="contained"
                        color="error"
                        sx={{
                            backgroundColor: "#d32f2f",
                            "&:hover": {
                                backgroundColor: "#b71c1c",
                            },
                            "&:disabled": {
                                backgroundColor: "#666",
                            }
                        }}
                    >
                        {deleteLoading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Deleting...
                            </>
                        ) : (
                            "DELETE MY PROFILE FOREVER"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Settings;